<template>
  <portal to="global-modal-portal" v-if="showReport">
    <b-modal :active.sync="showReport" :width="modalWidth" scroll="keep" :has-modal-card="true" :canCancel="false" id="carfax-report-modal">
      <div class="modal-card">
        <header class="modal-card-head">
            <p class="modal-card-title">CarFax Report</p>
        </header>
        <section class="modal-card-body">
          <error-display-component :serverErrors="serverErrors" :serverErrorMessage="serverErrorMessage"></error-display-component>
            <div class="aligner-item" style="text-align:center" v-if="isLoadingReport">
              <img src="@/assets/ajax-loader.gif">
            </div>
            <div class="content" v-if="!hasErrors && carFaxReportUrl && !isLoadingReport">
              <iframe :src="carFaxReportUrl" class="content" frameborder="0"></iframe>
            </div>
            <div class="column is-12" v-else-if="!hasErrors && !isLoadingReport && !carFaxReportUrl">
              <label class="label refresh-status-label">
              No CarFax report available
              </label>
            </div>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-danger" :class="{'is-loading': isGeneratingReport }" @click="hideReportModal" type="button">Close</button>
          <button class="button" :class="{'is-loading': isGeneratingReport }" @click="processReportGeneration" v-if="showButtons && carFaxReportUrl">Download</button>
          <button class="button is-info" @click="onPreviewLatestExistingReport" v-if="showButtons && latestCarfaxReportUrl">View latest stored report</button>
        </footer>
      </div>
    </b-modal>
  </portal>
  <pdf-modal
      :pdfFile="carFaxSelectedPdf"
      :usePortal="true"
      :portalOrder="2"
      v-on:on-pdf-downloaded="onPreviewed"
      v-on:on-pdf-closed="onPreviewClosed"
      v-else-if="carFaxSelectedPdf && carFaxSelectedPdf.src">
  </pdf-modal>
</template>

<script>

import { createNamespacedHelpers } from 'vuex'
import appraisalService from '@/services/appraisalService'
import ErrorDisplayComponent from '@/components/generic/ErrorDisplayComponent'
import PdfModalDisplayComponent from '@/components/generic/PdfModalDisplayComponent'
import utilitiesMixin from '@/mixins/generic/utilities'
import { hasCarfaxTesterRole } from '@/utils/carfax_roles'

const mapDealerState = createNamespacedHelpers('dealer').mapState
const mapUserGetters = createNamespacedHelpers('user').mapGetters

export default {
  mixins: [utilitiesMixin],
  name: 'CarFaxReportControl',
  components: {
    'error-display-component': ErrorDisplayComponent,
    'pdf-modal': PdfModalDisplayComponent
  },
  props: {
    carFaxReportUrl: {
      type: String,
      default: ''
    },
    showCarFaxReportModal: {
      type: Boolean,
      default: false
    },
    currentInventoryId: {
      type: Number,
      default: null
    },
    vinNo: {
      type: String,
      default: ''
    },    
    latestCarfaxReportUrl: {
      type: String,
      default: ''
    },
    /*
     * carFaxReportUrl is a stored PDF this control is being asked to display, not a live CARFAX link.
     *
     * ⚠ It only changes DOWNLOAD. The iframe renders either one unchanged; what breaks is Download,
     * which otherwise runs generateReportWithUrl against a file that is already a PDF.
     *
     * ⚠ It is NOT an access decision. Whether a stored PDF may be shown at all is settled by the
     * CARFAX-tester rule in CarFaxControl.openStoredCarfaxReportPdf, before this control is handed
     * anything - the iframe below has no role test of its own.
     */
    isStoredPdf: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      carfaxLink: true,
      showReport: false,
      isLoadingReport: false,
      serverErrorMessage: '',
      serverErrors: [],
      modalWidth: 960,
      isGeneratingReport: false,
      carFaxSelectedPdf: null,
      downloadUrl: ''
    }
  },
  computed: {
    ...mapUserGetters(['currentUser']),
    ...mapDealerState(['currentDealer']),
    urlVinNo: function () {
      var str = this.carFaxReportUrl
      var n = str.lastIndexOf('&vin=')
      var result = str.substring(n + 5)
      return result
    },
    hasErrors: function () {
      if (this.serverErrors.length > 0 || this.serverErrorMessage.length > 0) {
        return true
      }
      return false
    },
    isLegacyReport: function () {
      let carFaxReportUrl = this.carFaxReportUrl
      if (carFaxReportUrl && carFaxReportUrl.includes('https://www.carfaxonline.com/cfm/Display_Dealer_Report.cfm?partner=VAU_0&UID=')) {
        return true
      }
      return false
    },
    /*
     * ⚠ The SHARED predicate, not a fourth inline copy. utils/carfax_roles.js exists precisely because
     * several screens have to agree about this, and this file carried its own duplicate that never
     * converged. Behaviour is identical, including failing CLOSED when roles have not loaded.
     */
    hasCarfaxTesterRole: function () {
      return hasCarfaxTesterRole(this.currentUser)
    },
    showButtons: function () {
      if (this.hasCarfaxTesterRole) {
        return false
      }
      return true
    }
  },
  methods: {
    showReportModal: function () {
      this.showReport = true
    },
    hideReportModal: function () {
      this.$emit('on-hide-carfax-report')
      this.showReport = false
    },
    setMaxModalWidth: function () {
      let viewWidth = window.innerWidth
      let newWidth = viewWidth * 90 / 100
      this.modalWidth = newWidth
    },
    generateReport () {
      this.isGeneratingReport = true
      let that = this
      appraisalService.generateCarFaxReportPdf(this.vinNo).then(response => {
        if (response.errors) {
          that.serverErrors = response.errors
          that.isGeneratingReport = false
        } else {
          that.downloadUrl = response.carFaxPdfUrl
          if (that.downloadUrl) {
            that.isGeneratingReport = false
            that.showReport = false
            that.onPreview()
          } else {
            that.isGeneratingReport = false
            that.failedToast('PDF generation failed.')
          }
        }
      }).catch((error) => {
        that.isGeneratingReport = false
        that.serverErrorMessage = error.message
      })
    },
    generateReportWithUrl () {
      this.isGeneratingReport = true
      let carfaxUrl = this.carFaxReportUrl
      let vinNo = this.vinNo
      let inventoryId = this.currentInventoryId ?? 0
      let userId = this.currentUser.id
      let that = this

      appraisalService.carFaxReportUrlGeneratePdf(userId, carfaxUrl, vinNo, inventoryId).then(response => {
        if (response.errors) {
          that.serverErrors = response.errors
          that.isGeneratingReport = false
        } else {
          that.downloadUrl = response.carFaxPdfUrl
          if (that.downloadUrl) {
            that.isGeneratingReport = false
            that.showReport = false
            that.onPreview()
          } else {
            that.isGeneratingReport = false
            that.failedToast('PDF generation failed.')
          }
        }
      }).catch((error) => {
        that.isGeneratingReport = false
        that.serverErrorMessage = error.message
      })
    },
    onPreview: function () {
      var extractedFileName = this.downloadUrl.split('/').pop()
      var docUrl = process.env.VUE_APP_ROOT_API + 'document/download?url=' + encodeURI(this.downloadUrl)
      docUrl = encodeURI(docUrl)
      this.carFaxSelectedPdf = {
        name: 'Carfax Report',
        src: docUrl,
        fileName: extractedFileName,
        key: this.$uuid.v4()
      }
    },
    onPreviewed: function () {
    },
    onPreviewClosed: function () {
      this.carFaxSelectedPdf = null
    },
    processReportGeneration: function () {
      //⚠ Already a PDF - there is nothing to generate, so hand the operator the file itself. Running
      //generateReportWithUrl here would ask the server to render a report FROM a rendered report.
      if (this.isStoredPdf === true) {
        this.downloadStoredPdf()
        return
      }
      if (this.isLegacyReport === true) {
        this.generateReport()
      } else {
        this.generateReportWithUrl()
      }
    },
    /*
     * ⚠ Reuses the existing <pdf-modal> route rather than a bare window.open, so Download behaves the
     * same as it does for the stored report already reachable from this footer - and so a blocked
     * popup cannot swallow it.
     */
    downloadStoredPdf: function () {
      this.carFaxSelectedPdf = {
        name: 'Carfax Report',
        src: this.carFaxReportUrl,
        fileName: decodeURIComponent(String(this.carFaxReportUrl).split('/').pop()),
        key: this.$uuid.v4()
      }
      this.hideReportModal()
    },
    onPreviewLatestExistingReport: function () {
      this.downloadUrl = this.latestCarfaxReportUrl
      var extractedFileName = this.downloadUrl.split('/').pop()
      var docUrl = process.env.VUE_APP_ROOT_API + 'document/download?url=' + encodeURI(this.downloadUrl)
      docUrl = encodeURI(docUrl)
      this.carFaxSelectedPdf = {
        name: 'Carfax Report',
        src: docUrl,
        fileName: extractedFileName,
        key: this.$uuid.v4()
      }
      this.hideReportModal()
    }
  },
  watch: {
    showCarFaxReportModal: function (newValue, oldValue) {
      if (newValue) {
        this.showReportModal()
      }
    }
  },
  mounted: function () {
    this.setMaxModalWidth()
    let that = this
    window.onresize = () => {
      that.setMaxModalWidth()
    }
  }
}

</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

  .status-icon {
    width: 13px;
    height: 12px;
    margin-right:12px;
  }

  .carfax-container-display-only {
    display: inline-flex;
    flex-flow: row;
    align-items: center;
    margin-top:10px;
  }

  .carfax-container {
    margin-top: 20px;
    padding: 0;
    margin-left:0;
    margin-right:0;
  }

  .carfax-owner-icons {
    height: 12px;
    width: 12px;
    margin-top:-15px;
  }

  .carfax-warning {
    background-image: url('@/assets/static/carfax/ico_accident.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-danger {
    background-image: url('@/assets/static/carfax/ico_alert.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-recall {
    background-image: url('@/assets/static/carfax/ico_recall.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .carfax-ok {
    background-image: url('@/assets/static/carfax/ico_green_check.png');
    background-repeat: no-repeat, no-repeat;
    margin-top:-15px;
  }

  .refresh-status-label {
    margin-left:1em;
  }

  .cd .column {
    padding:0;
  }

  .modal-card-body {
    position:relative;
    max-height: calc(100vh - 220px);
    max-width: 90vw !important;
    width: 90vw !important;
    height: calc(100vh - 220px);
    overflow-y: auto;
    background: white;
  }
  .modal-card {
    max-width: 90vw !important;
    width: 90vw !important;
  }
  .content {
    display: inline-block;
    height: 100%;
    width: 100%;
  }

</style>
